import { useState, useMemo } from 'react';
import { ArrowUpDown, Filter } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AdminTabShell } from './AdminTabShell';
import {
  type DataQualityOverview,
  type PropertyQualityRow,
} from '@/hooks/admin/useAdminDataQuality';
import {
  TIER_LABELS,
  TIER_BADGE_CLASSES,
  PV_HEALTH_LABELS,
  PV_HEALTH_CLASSES,
  formatPercent,
  formatScore,
  reasonCodesToLabels,
} from '@/lib/data-quality-labels';

type SortField = 'score' | 'name' | 'tier' | 'coverage' | 'fallback';
type SortDir = 'asc' | 'desc';
type TierFilter = 'all' | 'poor_unusable' | 'watch' | 'good_excellent';

const TIER_ORDER: Record<string, number> = {
  unusable: 0,
  poor: 1,
  watch: 2,
  good: 3,
  excellent: 4,
};

interface DataQualityTabProps {
  data: DataQualityOverview | null;
  isLoading: boolean;
  error: Error | null;
}

function TierBadge({ tier }: { tier: string | null }) {
  const t = tier ?? 'unscored';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${TIER_BADGE_CLASSES[t] ?? TIER_BADGE_CLASSES.unscored}`}>
      {TIER_LABELS[t] ?? t}
    </span>
  );
}

function TierDistribution({ data }: { data: Record<string, number> }) {
  const order = ['excellent', 'good', 'watch', 'poor', 'unusable', 'unscored'];
  const entries = order.filter(k => data[k]).map(k => [k, data[k]] as [string, number]);
  if (entries.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {entries.map(([tier, count]) => (
        <span key={tier} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${TIER_BADGE_CLASSES[tier] ?? TIER_BADGE_CLASSES.unscored}`}>
          {TIER_LABELS[tier] ?? tier}: {count}
        </span>
      ))}
    </div>
  );
}

function SortHeader({
  label,
  field,
  currentField,
  currentDir,
  onSort,
}: {
  label: string;
  field: SortField;
  currentField: SortField;
  currentDir: SortDir;
  onSort: (f: SortField) => void;
}) {
  const active = field === currentField;
  return (
    <th
      className="text-left py-2 px-3 font-medium cursor-pointer select-none hover:text-wt-text-primary transition-colors"
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={`h-3 w-3 ${active ? 'text-wt-text-primary' : 'text-wt-text-disabled'}`} />
        {active && <span className="text-[9px]">{currentDir === 'asc' ? '\u2191' : '\u2193'}</span>}
      </span>
    </th>
  );
}

export function DataQualityTab({ data, isLoading, error }: DataQualityTabProps) {
  const [sortField, setSortField] = useState<SortField>('score');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [tierFilter, setTierFilter] = useState<TierFilter>('all');

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'name' ? 'asc' : 'asc');
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.properties;

    if (tierFilter === 'poor_unusable') {
      rows = rows.filter(r => r.data_quality_tier === 'poor' || r.data_quality_tier === 'unusable');
    } else if (tierFilter === 'watch') {
      rows = rows.filter(r => r.data_quality_tier === 'watch');
    } else if (tierFilter === 'good_excellent') {
      rows = rows.filter(r => r.data_quality_tier === 'good' || r.data_quality_tier === 'excellent');
    }

    return rows;
  }, [data, tierFilter]);

  const sorted = useMemo(() => {
    const cmp = (a: PropertyQualityRow, b: PropertyQualityRow): number => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortField) {
        case 'score':
          return ((a.data_quality_score ?? -1) - (b.data_quality_score ?? -1)) * dir;
        case 'name':
          return a.property_name.localeCompare(b.property_name) * dir;
        case 'tier':
          return ((TIER_ORDER[a.data_quality_tier ?? ''] ?? -1) - (TIER_ORDER[b.data_quality_tier ?? ''] ?? -1)) * dir;
        case 'coverage':
          return ((a.telemetry_coverage_7d ?? -1) - (b.telemetry_coverage_7d ?? -1)) * dir;
        case 'fallback':
          return ((a.fallback_rate_7d ?? -1) - (b.fallback_rate_7d ?? -1)) * dir;
        default:
          return 0;
      }
    };
    return [...filtered].sort(cmp);
  }, [filtered, sortField, sortDir]);

  return (
    <AdminTabShell isLoading={isLoading} error={error}>
      {data && (
        <div className="space-y-4">
          {/* Tier distribution summary */}
          <TierDistribution data={data.tierDistribution} />

          {/* Filter controls */}
          <div className="flex items-center gap-2">
            <Filter className="h-3.5 w-3.5 text-wt-text-muted" />
            <span className="text-xs text-wt-text-muted">Filter:</span>
            {(['all', 'poor_unusable', 'watch', 'good_excellent'] as TierFilter[]).map(f => (
              <Button
                key={f}
                variant={tierFilter === f ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTierFilter(f)}
                className="text-xs h-7 px-2"
              >
                {{
                  all: `All (${data.properties.length})`,
                  poor_unusable: `Poor/Unusable (${data.properties.filter(r => r.data_quality_tier === 'poor' || r.data_quality_tier === 'unusable').length})`,
                  watch: `Watch (${data.properties.filter(r => r.data_quality_tier === 'watch').length})`,
                  good_excellent: `Good/Excellent (${data.properties.filter(r => r.data_quality_tier === 'good' || r.data_quality_tier === 'excellent').length})`,
                }[f]}
              </Button>
            ))}
          </div>

          {/* Table */}
          {sorted.length === 0 ? (
            <div className="text-center py-12 text-sm text-wt-text-muted">
              No properties match the current filter.
            </div>
          ) : (
            <div className="border border-wt-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-wt-bg-tertiary text-wt-text-muted text-xs uppercase tracking-wider">
                      <SortHeader label="Property" field="name" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                      <th className="text-left py-2 px-3 font-medium">Org</th>
                      <SortHeader label="Tier" field="tier" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                      <SortHeader label="Score" field="score" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                      <SortHeader label="Coverage 7d" field="coverage" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                      <SortHeader label="Fallback 7d" field="fallback" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                      <th className="text-left py-2 px-3 font-medium">PV Learning</th>
                      <th className="text-left py-2 px-3 font-medium">Reason Codes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-wt-border">
                    {sorted.map(row => (
                      <tr key={row.property_id} className="hover:bg-wt-bg-secondary/50">
                        <td className="py-2 px-3">
                          <a
                            href={`/app/property/${row.property_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-wt-text-primary hover:underline"
                          >
                            {row.property_name}
                          </a>
                        </td>
                        <td className="py-2 px-3 text-wt-text-secondary">{row.org_name}</td>
                        <td className="py-2 px-3"><TierBadge tier={row.data_quality_tier} /></td>
                        <td className="py-2 px-3 font-mono">{formatScore(row.data_quality_score)}</td>
                        <td className="py-2 px-3 font-mono">{formatPercent(row.telemetry_coverage_7d)}</td>
                        <td className="py-2 px-3 font-mono">{formatPercent(row.fallback_rate_7d)}</td>
                        <td className="py-2 px-3">
                          <span className={PV_HEALTH_CLASSES[row.pv_learning_health ?? ''] ?? 'text-wt-text-muted'}>
                            {PV_HEALTH_LABELS[row.pv_learning_health ?? ''] ?? '—'}
                          </span>
                        </td>
                        <td className="py-2 px-3 max-w-[280px]">
                          <ReasonCodeBadges codes={row.quality_reason_codes} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </AdminTabShell>
  );
}

function ReasonCodeBadges({ codes }: { codes: string[] | null }) {
  const labels = reasonCodesToLabels(codes);
  if (labels.length === 0) return <span className="text-wt-text-muted">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {labels.map((label, i) => (
        <Badge key={i} variant="outline" className="text-[10px] font-normal whitespace-nowrap">
          {label}
        </Badge>
      ))}
    </div>
  );
}
